import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to detect if the user is on a mobile or tablet device
 * @returns {Object} { isMobile, isTablet, isDesktop }
 */
export const useDeviceDetection = () => {
	// Helper function to check pointer type (coarse = touch device)
	const hasCoarsePointer = () => {
		if (typeof window !== 'undefined' && window.matchMedia) {
			return window.matchMedia('(any-pointer: coarse)').matches;
		}
		return false;
	};

	// Helper function to detect device type
	// Based on CSS-Tricks standard device breakpoints:
	// Mobile: < 768px
	// Tablet: 768px - 1024px (standard tablet range)
	// Desktop: > 1024px
	const detectDevice = () => {
		const userAgent = navigator.userAgent || navigator.vendor || window.opera;
		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;
		const screenWidth = window.screen.width;
		const screenHeight = window.screen.height;

		// Check for touch capability
		const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
		
		// Check for coarse pointer (better tablet detection as per CSS-Tricks)
		const hasCoarsePointerDevice = hasCoarsePointer();

		// iPad detection (including iPad Pro with iPadOS 13+)
		const isIPad = 
			(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
			/iPad/.test(userAgent);

		// Check for tablets via user agent
		const isTabletUserAgent = 
			/(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/i.test(
				userAgent.toLowerCase()
			);

		// Check for mobile devices via user agent
		const isMobileUserAgent = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(
			userAgent.toLowerCase()
		);

		// Check for desktop OS indicators (Windows, Mac, Linux - not mobile/tablet)
		const isDesktopOS = /(win|mac|linux|x11)/i.test(navigator.platform) && 
			!isMobileUserAgent && 
			!isTabletUserAgent;

		// Detect if viewport is significantly smaller than screen (resized window)
		const isResizedWindow = screenWidth > viewportWidth + 100 || screenHeight > viewportHeight + 100;

		// Standard breakpoints:
		// Mobile: < 768px
		// Tablet: 768px - 1023px
		// Desktop: >= 1024px (1024px is laptop width)
		
		if (viewportWidth < 768) {
			// Small screen - mobile device (< 768px)
			// Check if it's a tablet in portrait mode first
			if (isIPad || isTabletUserAgent) {
				return {
					isMobile: false,
					isTablet: true,
					isDesktop: false,
				};
			}
			// Default to mobile for very small screens
			return {
				isMobile: true,
				isTablet: false,
				isDesktop: false,
			};
		} else if (viewportWidth >= 768 && viewportWidth < 1024) {
			// Tablet range (768px - 1023px)
			// Need to distinguish actual tablets from desktop browsers
			
			// Priority 1: Desktop OS = desktop (laptop in small window)
			// Only exception is iPad which can run on desktop OS
			if (isDesktopOS) {
				// iPad is the only exception - it can appear as desktop OS but is actually a tablet
				if (isIPad) {
					return {
						isMobile: false,
						isTablet: true,
						isDesktop: false,
					};
				}
				// Desktop OS in small window = laptop/desktop
				return {
					isMobile: false,
					isTablet: false,
					isDesktop: true,
				};
			}
			
			// Priority 2: Definitely a tablet (iPad, tablet UA, or coarse pointer)
			if (isIPad || isTabletUserAgent || (hasCoarsePointerDevice && !isMobileUserAgent)) {
				return {
					isMobile: false,
					isTablet: true,
					isDesktop: false,
				};
			}
			
			// Priority 3: Touch device in tablet range = tablet
			if (hasTouchScreen && !isMobileUserAgent) {
				return {
					isMobile: false,
					isTablet: true,
					isDesktop: false,
				};
			}
			
			// Default to tablet for 768-1023px range (covers actual tablet cases)
			return {
				isMobile: false,
				isTablet: true,
				isDesktop: false,
			};
		} else {
			// Desktop range (>= 1024px) - 1024px is laptop width
			// Only consider it a tablet if it's definitely a touch device (iPad Pro, etc.)
			if (isIPad || (hasCoarsePointerDevice && isTabletUserAgent && !isDesktopOS)) {
				return {
					isMobile: false,
					isTablet: true,
					isDesktop: false,
				};
			}
			// 1024px and above = desktop/laptop
			return {
				isMobile: false,
				isTablet: false,
				isDesktop: true,
			};
		}
	};

	// Initialize with actual detection immediately
	const [deviceInfo, setDeviceInfo] = useState(detectDevice);
	
	// Use ref to track previous device type without causing re-renders
	const previousDeviceTypeRef = useRef(
		deviceInfo.isMobile ? 'mobile' : deviceInfo.isTablet ? 'tablet' : 'desktop'
	);

	useEffect(() => {
		const checkDevice = () => {
			const newDeviceInfo = detectDevice();
			const currentDeviceType = newDeviceInfo.isMobile ? 'mobile' : newDeviceInfo.isTablet ? 'tablet' : 'desktop';
			const previousDeviceType = previousDeviceTypeRef.current;

			// If device type changed, reload the app
			if (previousDeviceType !== currentDeviceType) {
				console.log(`Device type changed from ${previousDeviceType} to ${currentDeviceType}. Reloading app...`);
				previousDeviceTypeRef.current = currentDeviceType;
				window.location.reload();
				return;
			}

			// Update state only if values actually changed
			setDeviceInfo((prevInfo) => {
				if (
					prevInfo.isMobile !== newDeviceInfo.isMobile ||
					prevInfo.isTablet !== newDeviceInfo.isTablet ||
					prevInfo.isDesktop !== newDeviceInfo.isDesktop
				) {
					return newDeviceInfo;
				}
				return prevInfo;
			});
		};

		// Add resize listener with debounce to avoid excessive checks
		let resizeTimeout;
		const handleResize = () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(checkDevice, 150);
		};

		window.addEventListener('resize', handleResize);

		// Cleanup
		return () => {
			clearTimeout(resizeTimeout);
			window.removeEventListener('resize', handleResize);
		};
	}, []); // Empty dependency array - only run once on mount

	return deviceInfo;
};
